import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Tracks how many in-app navigations have happened during the current
 * browser tab's lifetime. The back button uses this to decide whether
 * `Location.back()` will land on a real SPA route (we've navigated at
 * least once) or whether it should fall back to a safe default route
 * (the user opened a deep link directly, e.g. from email or a paste).
 *
 * Provided in root and bootstrapped from `AppComponent` so the counter
 * starts at app boot, before any consumer reads it.
 */
@Injectable({ providedIn: 'root' })
export class NavigationHistoryService {
  /** Count of NavigationEnd events seen so far. >=2 means there's a previous SPA route. */
  private navCount = 0;

  /** Last fully-resolved url, useful for diagnostics. */
  private lastUrl: string | null = null;

  constructor(private router: Router) {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.navCount += 1;
        this.lastUrl = e.urlAfterRedirects || e.url;
      });
  }

  /** True when going back via `Location.back()` will land on a previous SPA route. */
  hasInAppHistory(): boolean {
    return this.navCount > 1;
  }

  /** Number of NavigationEnd events seen so far. */
  getNavCount(): number {
    return this.navCount;
  }

  /** The most recent fully-resolved URL the router navigated to. */
  getLastUrl(): string | null {
    return this.lastUrl;
  }
}
